import type {ReactElement} from 'react';
import {useEffect, useRef, useState} from 'react';
import {InputField} from '@Components/input-field-v2';
import type {InputFieldControlItemData} from '@Components/input-field-v2/input-field.types';
import {InputControlItemType} from '@Components/input-field-v2/input-field.types';
import {Popover} from '@Components/popover';
import {PopoverDefaultAlignment} from '@Components/popover/popover.types';
import {PosterEditorTestId} from '@/test-ids/poster-editor-test-id';
import {StockPanelRecentSearchesDropdown} from '../stock-panel-recent-searches-dropdown/stock-panel-recent-searches-dropdown';
import {RECENT_SEARCHES_DROPDOWN_OFFSET} from '../stock-panel-recent-searches-dropdown/stock-panel-recent-searches-dropdown.types';
import {clearRecentSearches, getMatchingRecentSearches, loadRecentSearches, saveRecentSearch} from '../../stock-panel-recent-searches-library';
import type {StockPanelSearchBarProps} from './stock-panel-search-bar.types';
import {SEARCH_CONTROL_ID} from './stock-panel-search-bar.types';
import styles from './stock-panel-search-bar.module.scss';

export function StockPanelSearchBar(props: StockPanelSearchBarProps): ReactElement {
  const [inputValue, setInputValue] = useState(props.val ?? '');
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setInputValue(props.val ?? '');
  }, [props.val]);

  useEffect(() => {
    props.onInputTextChange?.(inputValue);
  }, [inputValue]);

  useEffect(() => {
    let isMounted = true;
    void loadRecentSearches().then((searches): void => {
      if (isMounted) {
        setRecentSearches(searches);
      }
    });
    return (): void => {
      isMounted = false;
    };
  }, []);

  const matchingRecentSearches = getMatchingRecentSearches(recentSearches, inputValue);

  const commitSearch = (searchTerm: string): void => {
    const trimmed = searchTerm.trim();
    if (trimmed) {
      setRecentSearches(saveRecentSearch(trimmed));
    }
    setIsDropdownVisible(false);
    props.onSearch(trimmed);
  };

  const onRecentSearchSelected = (term: string): void => {
    setInputValue(term);
    setIsDropdownVisible(false);
    setRecentSearches(saveRecentSearch(term));
    props.onSearch(term);
  };

  const onClearRecentSearches = (): void => {
    setRecentSearches(clearRecentSearches());
    setIsDropdownVisible(false);
  };

  const getControls = (): InputFieldControlItemData[] => {
    const controls: InputFieldControlItemData[] = [];
    if (inputValue !== '') {
      controls.push({
        id: SEARCH_CONTROL_ID.CANCEL,
        icon: 'icon-close',
        type: InputControlItemType.ICONS_ONLY,
      });
    }
    controls.push({
      id: SEARCH_CONTROL_ID.SEARCH,
      icon: 'icon-search',
      isLoading: props.isLoading,
      type: inputValue !== '' ? InputControlItemType.ICON_BTNS : InputControlItemType.ICONS_ONLY,
    });
    return controls;
  };

  const onControlClick = (id: string): void => {
    if (id === SEARCH_CONTROL_ID.CANCEL) {
      setInputValue('');
      setIsDropdownVisible(false);
      props.onBack?.();
      return;
    }
    commitSearch(inputValue);
  };

  const getRecentSearchesDropdown = (): ReactElement | null => {
    if (!isDropdownVisible || matchingRecentSearches.length === 0) {
      return null;
    }
    return (
      <Popover
        referenceElementRef={containerRef}
        popoverCloseFunction={(): void => {
          setIsDropdownVisible(false);
        }}
        alignment={PopoverDefaultAlignment.BOTTOM_START}
        popoverOffset={RECENT_SEARCHES_DROPDOWN_OFFSET}
        shouldInheritReferenceElementWidth
        hasAutoPlacement={false}
        scrollAnchorIntoView={false}
        className={styles.recentSearchesPopover}
      >
        <StockPanelRecentSearchesDropdown searchTerms={matchingRecentSearches} onClear={onClearRecentSearches} onTermClick={onRecentSearchSelected} />
      </Popover>
    );
  };

  return (
    <div ref={containerRef} className={`${styles.container} ${props.className ?? ''}`} data-testid={PosterEditorTestId.STOCK_PANEL_SEARCH_BAR}>
      <InputField
        isControlled
        input={inputValue}
        placeholder={props.placeholderText}
        onInputFocus={(): void => {
          setIsDropdownVisible(true);
        }}
        onInputChange={(value): void => {
          const text = String(value);
          setInputValue(text);
          if (!text) {
            props.onSearch('');
          }
        }}
        onInputSubmit={(): void => {
          commitSearch(inputValue);
        }}
        controls={{
          showDefaultControls: false,
          showSingleDefaultControlOnly: false,
          customControls: getControls(),
          onClick: onControlClick,
        }}
      />
      {getRecentSearchesDropdown()}
    </div>
  );
}
